import { H5 } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/styled';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Text, Badge, Flex } from '@mantine/core';
import {
	IconContainer,
	SelectedFileItemContainer,
	Container,
} from '@dealstack/frontend/shared/components/Documents/UploadDocuments/AddFileStep/styled';
import { FileWithClassifier } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/AddFileStep/types';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { HeaderContainer } from '@dealstack/frontend/shared/components/Documents/styled';
import { useMemo, useState } from 'react';
import { Paginate } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/components/Paginate';
import {
	DOCUMENT_LIBRARY_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds';

interface Props {
	uploadFileData: FileWithClassifier[];
	setUploadFileData: React.Dispatch<React.SetStateAction<FileWithClassifier[]>>;
}

export const SelectedFiles = ({ uploadFileData, setUploadFileData }: Props) => {
	const batchSize = 8;
	const tCapManage = useTranslationCapManage();
	const [pageIndex, setPageIndex] = useState(0);

	const handleRemoveFile = (file: FileWithClassifier) => {
		setUploadFileData(
			uploadFileData.filter((uploadedFile) => uploadedFile.file.name !== file.file.name)
		);
		showToastV2({
			title: tCapManage('document.upload_state.uploaded_section.remove_file.title'),
			message: `${tCapManage('document.upload_state.uploaded_section.remove_file.message')}`,
			state: 'success',
		});
	};

	const batches = useMemo(() => {
		const chunks: FileWithClassifier[][] = [];
		for (let i = 0; i < uploadFileData.length; i += batchSize) {
			chunks.push(uploadFileData.slice(i, i + batchSize));
		}
		return chunks;
	}, [uploadFileData]);

	const currentBatch = batches[pageIndex] || [];

	return (
		<Container
			id="selected-files"
			data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_SELECTED_FILES}
		>
			<HeaderContainer id="selected-files-header">
				<H5>
					{tCapManage('document.upload_state.uploaded_section.header')}(
					{uploadFileData.length})
				</H5>
				<Paginate data={batches} onClick={setPageIndex} pageIndex={pageIndex} />
			</HeaderContainer>
			<Flex wrap="wrap" gap="0.5rem" mb="1rem">
				{currentBatch.map((file) => (
					<SelectedFileItemContainer
						key={file.file.name}
						data-testid={makeComplexDataTestId(
							DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_SELECTED_FILE,
							file.file.name
						)}
					>
						<IconContainer $justify="flex-end">
							<Icon
								dataTestId={makeComplexDataTestId(
									DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_SELECTED_FILE_REMOVE,
									file.file.name
								)}
								onClick={() => handleRemoveFile(file)}
								size="0.875rem"
								variant="trash-can"
								color="#F20D2B"
								style={{ cursor: 'pointer' }}
							/>
						</IconContainer>
						<IconContainer $justify="flex-start">
							<Icon size="1.25rem" variant="document-v3" />
							<Text
								style={{
									wordBreak: 'break-all',
									fontWeight: 500,
									paddingLeft: '0.5rem',
								}}
							>
								{file.file.name}
							</Text>
						</IconContainer>
						<Badge
							data-testid={makeComplexDataTestId(
								DOCUMENT_LIBRARY_ELEMENT_ID.UPLOAD_SELECTED_FILE_CLASSIFIER,
								file.file.name
							)}
							variant="outline"
							color="dark.0"
							style={{ fontWeight: 400 }}
						>
							{file.classifierName}
						</Badge>
					</SelectedFileItemContainer>
				))}
			</Flex>
		</Container>
	);
};
